import Layout from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { PageDecorations } from "@/components/Decorations";
import { Mail, Facebook, Instagram, Linkedin, Send, Landmark } from "lucide-react";
import { TikTok } from "@/components/icons/TikTok";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { usePageContent } from "@/lib/usePageContent";

const FALLBACK = {
  hero_title: "Ota yhteyttä",
  email: "sinisellasydamella@gmail.com",
  social_title: "Seuraa miuta somessa!",
  org_name: "Sinisellä Sydämellä ry",
  form_title: "Yhteydenottolomake",
};

const Contact = () => {
  const { t } = usePageContent("contact", FALLBACK);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const fd = new FormData(e.currentTarget);
    const name = ((fd.get("name") as string) || "").trim();
    const email = ((fd.get("email") as string) || "").trim();
    const subject = ((fd.get("subject") as string) || "").trim();
    const message = ((fd.get("message") as string) || "").trim();
    const consent = fd.get("consent");
    if (!name || !email || !subject || !message) {
      toast.error("Täytä kaikki pakolliset kentät.");
      setIsSubmitting(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Anna kelvollinen sähköpostiosoite.");
      setIsSubmitting(false);
      return;
    }
    if (!consent) {
      toast.error("Anna suostumuksesi henkilötietojen käsittelyyn.");
      setIsSubmitting(false);
      return;
    }
    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: { name, email, subject, message },
      });
      if (error) throw error;
      toast.success("Kiitos viestistäsi! Palaan sinulle pian.");
      (e.target as HTMLFormElement).reset();
    } catch {
      toast.error("Viestin lähetys epäonnistui. Yritä uudelleen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const emailAddr = t("email");

  return (
    <Layout>
      <PageHero>{t("hero_title")}</PageHero>

      <section className="relative overflow-hidden px-6 py-16 lg:px-10" style={{ background: "linear-gradient(180deg, hsl(39,82%,92%) 0%, hsl(42,86%,78%) 100%)" }}>
        <PageDecorations />
        <div className="relative mx-auto grid max-w-5xl gap-12 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-2xl border border-border p-6 shadow-sm" style={{ background: "hsl(206 42% 90%)" }}>
              <h2 className="mb-4 font-display text-lg font-semibold text-foreground">
                {t("social_title")}
              </h2>
              <div className="flex gap-3">
                {[
                  { href: "https://www.instagram.com/ellosenlotta", icon: Instagram },
                  { href: "https://www.facebook.com/lottaellonen", icon: Facebook },
                  { href: "https://www.linkedin.com/in/lottaellonen", icon: Linkedin },
                  { href: "https://www.tiktok.com/@morrikka_", icon: TikTok },
                ].map(({ href, icon: Icon }, i) => (
                  <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-xl bg-secondary p-3 text-secondary-foreground transition-transform hover:-translate-y-0.5"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-border p-6 shadow-sm" style={{ background: "hsl(38 45% 93%)" }}>
              <p className="mb-4 font-semibold text-foreground">{t("org_name")}</p>
              <div className="flex items-center gap-4">
                <a
                  href={`mailto:${emailAddr}`}
                  className="rounded-xl bg-secondary p-3 transition-transform hover:-translate-y-0.5"
                  aria-label={emailAddr}
                >
                  <Mail className="h-5 w-5 text-secondary-foreground" />
                </a>
                <p className="select-text font-medium text-foreground cursor-text">{emailAddr}</p>
              </div>
              <div className="mt-3 flex items-center gap-4">
                <div className="rounded-xl bg-secondary p-3">
                  <Landmark className="h-5 w-5 text-secondary-foreground" />
                </div>
                <p className="font-medium text-foreground">FI57 5741 4020 1805 63</p>
              </div>
            </div>

            <div className="rounded-2xl border border-border p-6 shadow-sm" style={{ background: "hsl(340 28% 91%)" }}>
              <h2 className="mb-3 font-display text-lg font-semibold text-foreground">
                Liity mukaan tukitiimiin!
              </h2>
              <p className="leading-relaxed text-foreground/85">
                Pääset mukaan täyttämällä oheisen yhteydenottolomakkeen tai laittamalla Lotalle viestiä. Kerro meille nimesi, yhteystietosi, asuinkuntasi sekä mikä sinua innostaa politiikassa. Kaikki tuki on arvokasta ja tarpeen. Tule siis oppimaan yhdessä! Aiempaa kokemusta ei tarvita, halu osallistua riittää.
              </p>
            </div>
          </div>

          <div className="space-y-6 lg:col-span-3">
            <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
              <h2 className="mb-6 font-display text-2xl font-semibold text-foreground">
                {t("form_title")}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-2 block text-sm font-medium text-foreground">
                      Nimi&nbsp;<span className="text-primary">*</span>
                    </label>
                    <Input id="name" name="name" required className="border-border bg-background" />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground">
                      Sähköposti&nbsp;<span className="text-primary">*</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="border-border bg-background"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="mb-2 block text-sm font-medium text-foreground">
                    Aihe&nbsp;<span className="text-primary">*</span>
                  </label>
                  <Input id="subject" name="subject" required className="border-border bg-background" />
                </div>
                <div>
                  <label htmlFor="message" className="mb-2 block text-sm font-medium text-foreground">
                    Viesti&nbsp;<span className="text-primary">*</span>
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    className="resize-none border-border bg-background"
                  />
                </div>
                <div className="flex items-start gap-3">
                  <input
                    id="consent"
                    name="consent"
                    type="checkbox"
                    required
                    className="mt-1 h-4 w-4 cursor-pointer rounded border-border accent-secondary"
                  />
                  <label htmlFor="consent" className="text-sm text-foreground">
                    Annan suostumukseni henkilötietojeni käsittelyyn tietosuojaselosteen mukaisesti.&nbsp;<span className="text-primary">*</span>
                  </label>
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-full bg-primary py-6 font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  {isSubmitting ? (
                    "Lähetetään..."
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Ota yhteyttä
                    </>
                  )}
                </Button>
              </form>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4 text-sm shadow-sm">
              <a
                href="/tietosuoja"
                className="text-secondary underline-offset-4 hover:underline"
              >
                Tietosuojaseloste
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
